import React from 'react';
import { Box, Typography, Grid, Stack, Button, Container } from '@mui/material';
import { KingBed, Bathtub, Luggage, CheckCircle } from '@mui/icons-material';
import { motion } from 'framer-motion';

import hospitalityStandards from '../data/hospitalityStandards.json';

// JSON can't carry component references, so pillars declare an iconKey instead
const ICONS = {
  bed: KingBed,
  bath: Bathtub,
  welcome: Luggage,
};

/**
 * Editorial 3-pillar showcase of our short-term rental turnover technique.
 *
 * Renders only the inner grid — the calling page supplies its own section chrome
 * (SectionBlock on VacationRentals, ModernSectionBlock on Landing) so the block
 * stays visually native on both.
 *
 * @param {Array}  pillars - pillar data; defaults to src/data/hospitalityStandards.json
 * @param {Object} cta     - optional { label, onClick, href }
 * @param {boolean} animate - scroll-triggered fade/slide per row
 */
const HospitalityStandardSection = ({
  pillars = hospitalityStandards.pillars,
  cta = null,
  animate = true,
}) => (
  <Container maxWidth="lg" disableGutters>
    {pillars.map((pillar, index) => {
      const Icon = ICONS[pillar.iconKey];
      const imageFirst = index % 2 === 0;

      // Rows slide in from the side their image sits on
      const motionProps = animate
        ? {
            component: motion.div,
            initial: { opacity: 0, y: 24, x: imageFirst ? -20 : 20 },
            whileInView: { opacity: 1, y: 0, x: 0 },
            // amount stays low: rows stack on mobile and grow taller than the
            // viewport, and a threshold that can't be met leaves the row at
            // opacity 0 forever
            viewport: { once: true, amount: 0.15 },
            transition: { duration: 0.6, ease: 'easeOut' },
          }
        : {};

      return (
        <Box
          key={pillar.id}
          {...motionProps}
          sx={{ mb: { xs: 6, md: 10 }, '&:last-of-type': { mb: 0 } }}
        >
          <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
            <Grid
              item
              xs={12}
              md={6}
              sx={{ order: { xs: 1, md: imageFirst ? 1 : 2 } }}
            >
              <Box
                component="img"
                src={pillar.image}
                alt={pillar.alt}
                loading="lazy"
                decoding="async"
                width={1200}
                height={900}
                sx={{
                  width: '100%',
                  height: 'auto',
                  aspectRatio: '4 / 3',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  borderRadius: 3,
                  bgcolor: 'grey.100',
                  boxShadow: 3,
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{ order: { xs: 2, md: imageFirst ? 2 : 1 } }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                {Icon && <Box component={Icon} sx={{ fontSize: 22, color: 'gold.dark' }} />}
                <Typography
                  variant="overline"
                  sx={{ color: 'gold.dark', fontWeight: 700, letterSpacing: 1.2 }}
                >
                  {pillar.eyebrow}
                </Typography>
              </Stack>

              <Typography
                variant="h5"
                component="h3"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  mb: 2,
                  fontSize: { xs: '1.35rem', sm: '1.5rem', md: '1.75rem' },
                  lineHeight: 1.3,
                }}
              >
                {pillar.title}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
                  mb: 3,
                }}
              >
                {pillar.body}
              </Typography>

              <Stack spacing={1.25}>
                {pillar.bullets.map((bullet) => (
                  <Stack direction="row" alignItems="flex-start" spacing={1} key={bullet}>
                    <CheckCircle color="success" sx={{ fontSize: 20, mt: '2px', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                      {bullet}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      );
    })}

    {cta && (
      <Box sx={{ textAlign: 'center', mt: { xs: 5, md: 7 } }}>
        <Button
          variant="contained"
          size="large"
          href={cta.href}
          onClick={cta.onClick}
          sx={{ px: 4, fontWeight: 700 }}
        >
          {cta.label}
        </Button>
      </Box>
    )}
  </Container>
);

export default HospitalityStandardSection;
